import { createSelector, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import i18next from 'i18next';
import _ from '@lodash';

import navigationConfig from 'app/fuse-configs/navigation/navigationConfig';
import FuseUtils from '@fuse/utils';

const newBadge = {
  badge: {
    title: 'New',
    bg: 'linear-gradient(90deg, #4DB181 0%, #3F7284 100%)',
    fg: '#FFFFFF',
  },
};

const numberBadge = (number, color) => ({
  badge: {
    title: number,
    bg: color || '#FF3B30',
    fg: '#FFFFFF',
  },
});

const navigationAdapter = createEntityAdapter();
const emptyInitialState = navigationAdapter.getInitialState();
const initialState = navigationAdapter.upsertMany(emptyInitialState, navigationConfig);

export const appendNavigationItem = (item, parentId) => (dispatch, getState) => {
  const navigation = selectNavigationAll(getState());

  return dispatch(setNavigation(FuseUtils.appendNavItem(navigation, item, parentId)));
};

export const prependNavigationItem = (item, parentId) => (dispatch, getState) => {
  const navigation = selectNavigationAll(getState());

  return dispatch(setNavigation(FuseUtils.prependNavItem(navigation, item, parentId)));
};

export const updateNavigationItem = (id, item) => (dispatch, getState) => {
  const navigation = selectNavigationAll(getState());

  return dispatch(setNavigation(FuseUtils.updateNavItem(navigation, id, item)));
};

export const updateNavigationGroup = (id, item) => (dispatch, getState) => {
  const navigation = selectNavigationAll(getState());
  const group = navigation.find((_item) => _item.id === id);
  if (!group) return null;

  const groupItemsIds = group.children?.map((_item) => _item.id);
  if (!groupItemsIds) return null;

  return dispatch(setNavigation(FuseUtils.updateNavItems(navigation, groupItemsIds, item)));
};

export const updateNavigationBadge = (id, number, color) => (dispatch, getState) => {
  const navigation = selectNavigationAll(getState());

  const navItem = FuseUtils.findNavItem(navigation, id);

  const badge =
    typeof number === 'number' && number > 0
      ? numberBadge(number, color)
      : (navItem?.new && newBadge) || { badge: null };

  const updatedNavigation = FuseUtils.updateNavItem(navigation, id, badge);

  return dispatch(setNavigation(updatedNavigation));
};

export const removeNavigationItem = (id) => (dispatch, getState) => {
  const navigation = selectNavigationAll(getState());

  return dispatch(setNavigation(FuseUtils.removeNavItem(navigation, id)));
};

export const {
  selectAll: selectNavigationAll,
  selectIds: selectNavigationIds,
  selectById: selectNavigationItemById,
} = navigationAdapter.getSelectors((state) => state.fuse.navigation);

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    setNavigation: navigationAdapter.setAll,
    resetNavigation: (state, action) => initialState,
  },
});

export const { setNavigation, resetNavigation } = navigationSlice.actions;

export const selectUserRole = (state) => state.auth.user.roles;

function processNavItem(item) {
  const itemCopy = { ...item };

  // Translate title
  if (item.translate && item.title) {
    itemCopy.title = i18next.t(`navigation:${item.translate}`);
  }

  // Add new badge
  if (item.new && !item.badge) {
    itemCopy.badge = newBadge.badge;
  }

  return itemCopy;
}

export const selectNavigation = createSelector(
  [selectNavigationAll, ({ i18n }) => i18n.language, selectUserRole],
  (navigation, language, userRole) => {
    function recursiveProcessNavItems(data) {
      // loop through every object in the array
      return data.map((item) => {
        const processedItem = processNavItem(item);

        // see if there is a children node
        if (item.children) {
          // run this function recursively on the children array
          processedItem.children = recursiveProcessNavItems(item.children);
        }

        return processedItem;
      });
    }

    return recursiveProcessNavItems(
      _.merge(
        [],
        FuseUtils.filterRecursive(navigation, (item) => {
          return FuseUtils.hasPermission(item.auth, userRole);
        })
      )
    );
  }
);

export const selectFlatNavigation = createSelector([selectNavigation], (navigation) =>
  FuseUtils.getFlatNavigation(navigation)
);

export default navigationSlice.reducer;
